<script>
    import { slide } from "svelte/transition";
    import Icon from "fa-svelte";
    import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
    import { _ } from "svelte-i18n";
    import { createEventDispatcher } from "svelte";

    export let disabled = false;
    export let opened = false;
    export let options = [];
    export let value = null;
    export let filter = null;
    export let search = "";
    let clazz;
    export { clazz as class };

    const getSelectedOption = (value, options) =>
        options.find(({ value: it }) => value === it);

    $: selectedOption = getSelectedOption(value, options || []);

    let ref;

    let windowHeight;

    const PADDING_BOTTOM = 20;

    const dispatch = createEventDispatcher();

    $: refBoundingRect = ref && ref.getBoundingClientRect();
    $: yPos = refBoundingRect && refBoundingRect.top;
    $: reverse = yPos && windowHeight && yPos >= (windowHeight * 2) / 3;
    $: height = ref && ref.clientHeight;
    $: maxHeight =
        yPos &&
        height &&
        windowHeight &&
        getMaxHeight(windowHeight, yPos, PADDING_BOTTOM, height, reverse);

    const getMaxHeight = (
        windowHeight,
        yPos,
        PADDING_BOTTOM,
        height,
        reverse
    ) =>
        reverse
            ? yPos + height - PADDING_BOTTOM
            : windowHeight - (yPos + PADDING_BOTTOM + height);
    $: filteredOptions =
        filter && search ? getFilteredOptions(filter, search) : options;

    const getFilteredOptions = (filter, search) =>
        options.filter((...args) => filter(search, ...args));

    const handleSelect = (option) => {
        search = "";
        value = option.value;
        dispatch("change");
    };
</script>

<style>
    .select {
        position: relative;
    }

    .options {
        position: absolute;
        border: 1px solid #cccccc;
        border-top: none;
        border-radius: 0 0 10px 10px;
        left: 0;
        right: 0;
        z-index: 1000;
    }

    .options.reverse {
        border-radius: 10px 10px 0 0;
        border: 1px solid #cccccc;
        border-bottom: none;
    }

    .value {
        padding: 0.5em;
        border: 1px solid #cccccc;
        transition: border-radius 250ms ease-in-out;
        border-radius: 10px;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .value.opened {
        border-radius: 10px 10px 0 0;
    }

    .value.opened.reverse {
        border-radius: 0 0 10px 10px;
    }

    .icon {
        transition: transform 250ms ease-in-out;
    }

    .opened .icon {
        transform: rotateZ(-180deg);
    }

    .option {
        display: flex;
        flex-direction: column;
    }

    .options:not(.reverse) .option:not(:last-child) > span {
        border-bottom: 1px solid #cccccc;
    }

    .reverse .option:not(:first-child) > span {
        border-bottom: 1px solid #cccccc;
    }

    .options:not(.reverse) .option:last-child > span {
        border-radius: 0 0 10px 10px;
    }

    .reverse .option:first-child > span {
        border-radius: 10px 10px 0 0;
    }

    .option > span {
        padding: 0.5em;
        cursor: pointer;
    }
</style>

<svelte:window
    on:click={() => (opened = false)}
    bind:innerHeight={windowHeight} />

<div class={`select ${clazz || ''}`}>
    <div
        bind:this={ref}
        on:click|stopPropagation={!disabled && (() => (opened = !opened))}>
        <slot name="value" {opened}>
            <div
                class="value"
                class:opened
                class:reverse
                class:bg-gray-200={disabled}>
                {(selectedOption && selectedOption.text) || '-'}
                <div class="icon">
                    <Icon icon={faCaretDown} />
                </div>
            </div>
        </slot>
    </div>
    {#if opened}
        <div
            class={reverse ? 'options overflow-scroll flex flex-col-reverse' : 'options overflow-scroll flex flex-col'}
            class:reverse
            style={`${reverse ? 'bottom' : 'top'}: ${height}px; max-height: ${maxHeight}px;`}
            transition:slide>
            {#if filter}
                <input
                    class="option w-full border-b border-gray-200 p-2"
                    name="search"
                    autocomplete="off"
                    autocapitalize="off"
                    placeholder={$_('Search')}
                    on:click|stopPropagation
                    bind:value={search} />
            {/if}
            {#each filteredOptions as option}
                <div
                    class="option"
                    class:selected={option.value === value}
                    on:click={() => handleSelect(option)}>
                    <slot {option} selected={option.value === value}>
                        <span
                            class="bg-white hover:bg-blue-100"
                            class:bg-blue-200={option.value === value}>{option.text}</span>
                    </slot>
                </div>
            {/each}
        </div>
    {/if}
</div>
